import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { MovieCard } from "@/components/MovieCard";
import { PageToolbar, usePageTranslation } from "@/components/PageToolbar";

const INTRO = { title: "Explore Movies", sub: "Filter world cinema by country, genre, language and era — from festival masterpieces to local favorites." };

const selectCls = "bg-[#fbf8f1] border border-[#d8ccb8] rounded-full px-4 py-2 text-sm outline-none cursor-pointer focus:border-[#7d1a1a]";

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);

  const filters = {
    q: params.get("q") || "",
    country: params.get("country") || "",
    genre: params.get("genre") || "",
    language: params.get("language") || "",
    decade: params.get("decade") || "",
    sort: params.get("sort") || "rating",
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    Object.entries(filters).forEach(([k, v]) => v && (p[k] = v));
    api
      .get("/movies", { params: p })
      .then((r) => setMovies(r.data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const hasFilters = filters.q || filters.country || filters.genre || filters.language || filters.decade;
  const blocks = useMemo(() => INTRO, []);
  const { lang, tr, busy } = usePageTranslation(blocks, "explore-intro");

  return (
    <main data-testid="explore-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <p className="eyebrow">Global Discovery Engine</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl mt-2 text-[#1c1512]">{tr("title", INTRO.title)}</h1>
      <p className="text-[#6b5d51] mt-3 max-w-2xl">{tr("sub", INTRO.sub)}</p>
      <div className="mt-5">
        <PageToolbar speakText={`${tr("title", INTRO.title)}. ${tr("sub", INTRO.sub)}`} busy={busy} lang={lang} />
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-[#fbf8f1] border border-[#d8ccb8] rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-[#6b5d51]" />
          <input
            data-testid="explore-search-input"
            value={filters.q}
            onChange={(e) => setFilter("q", e.target.value)}
            placeholder="Title, director, actor…"
            className="bg-transparent text-sm outline-none w-44"
          />
        </div>
        <select data-testid="country-filter-select" className={selectCls} value={filters.country} onChange={(e) => setFilter("country", e.target.value)}>
          <option value="">All Countries</option>
          {meta?.countries.map((c) => (
            <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
          ))}
        </select>
        <select data-testid="genre-filter-select" className={selectCls} value={filters.genre} onChange={(e) => setFilter("genre", e.target.value)}>
          <option value="">All Genres</option>
          {meta?.genres.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select data-testid="language-filter-select" className={selectCls} value={filters.language} onChange={(e) => setFilter("language", e.target.value)}>
          <option value="">All Languages</option>
          {meta?.languages.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select data-testid="decade-filter-select" className={selectCls} value={filters.decade} onChange={(e) => setFilter("decade", e.target.value)}>
          <option value="">All Decades</option>
          {meta?.decades.map((d) => <option key={d} value={d}>{d}s</option>)}
        </select>
        <select data-testid="sort-select" className={selectCls} value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)}>
          <option value="rating">Top Rated</option>
          <option value="year">Newest</option>
          <option value="title">A–Z</option>
        </select>
        {hasFilters && (
          <button data-testid="clear-filters-button" onClick={() => setParams({}, { replace: true })} className="flex items-center gap-1 text-sm text-[#7d1a1a] font-medium hover:underline">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>

      <p data-testid="explore-result-count" className="font-mono-label text-xs tracking-[0.2em] uppercase text-[#8a7c6d] mt-8 mb-4">
        {loading ? "Loading…" : `${movies.length} film${movies.length === 1 ? "" : "s"} found`}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {movies.map((m) => <MovieCard key={m.id} movie={m} />)}
      </div>

      {!loading && movies.length === 0 && (
        <div data-testid="explore-empty-state" className="text-center py-20">
          <p className="font-display text-2xl text-[#6b5d51]">No films match those filters.</p>
          <p className="text-sm text-[#8a7c6d] mt-2">Try broadening your search — world cinema is vast.</p>
        </div>
      )}
    </main>
  );
}
